import React, { useState } from "react";
import {
  Package,
  Calendar,
  Users,
  TrendingUp,
  CheckCircle,
  Clock,
  Shield,
  BarChart3,
  Smartphone,
  Zap,
  FileText,
  Settings,
} from "lucide-react";
import PlansSection from "./PlansSection";
import "./LandingPage.css";

interface LandingPageProps {
  onNavigateToLogin: () => void;
  onNavigateToFuncionarioLogin?: () => void;
  onOpenPrivacy?: () => void;
  onOpenTerms?: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({
  onNavigateToLogin,
  onNavigateToFuncionarioLogin,
  onOpenPrivacy,
  onOpenTerms,
}) => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number>(0);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? -1 : index);
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="landing-header">
        <div className="landing-container">
          <div className="header-content">
            <div className="logo-landing">
              <h1 className="logo-landing-text">
                <span className="logo-system">System</span>
                <span className="logo-loc">Loc</span>
              </h1>
            </div>

            <nav className="landing-nav">
              <a href="#funcionalidades">Funcionalidades</a>
              {/* <a href="#planos">Planos</a> */}
              {/* <a href="#depoimentos">Depoimentos</a> */}
              <a href="#faq">FAQ</a>
            </nav>

            <div className="header-actions">
              <button
                className="btn-primary-header"
                onClick={onNavigateToLogin}
              >
                Entrar
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="landing-container">
          <div className="hero-content-wrapper">
            <div className="hero-text">
              <div className="hero-badge">🚀 Gestão inteligente de locação</div>
              <h1 className="hero-title">
                Gerencie suas{" "}
                <span className="hero-highlight">locações de roupas</span> com
                facilidade
              </h1>
              <p className="hero-subtitle">
                Controle estoque, reservas, devoluções e clientes em um único
                sistema completo. Simplifique sua operação e aumente seus
                resultados.
              </p>
              <div className="hero-cta">
                <button
                  className="btn-primary-large"
                  onClick={onNavigateToLogin}
                >
                  Começar agora gratuitamente →
                </button>
                {/* <button className="btn-secondary-large">
                  Ver demonstração
                </button> */}
              </div>
            </div>
            <div className="hero-image">
              <div className="dashboard-preview">
                <div className="preview-header">
                  <div className="preview-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div className="preview-url">systemloc.com/dashboard</div>
                </div>
                <div className="preview-content">
                  <div className="dashboard-metrics">
                    <div className="metric-card">
                      <div className="metric-label">Peças em estoque</div>
                      <div className="metric-value">1.248</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">Reservas ativas</div>
                      <div className="metric-value">87</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">Devoluções hoje</div>
                      <div className="metric-value">12</div>
                    </div>
                  </div>
                  <div className="dashboard-chart">
                    <div className="chart-bar" style={{ height: "60%" }}></div>
                    <div className="chart-bar" style={{ height: "75%" }}></div>
                    <div className="chart-bar" style={{ height: "65%" }}></div>
                    <div className="chart-bar" style={{ height: "85%" }}></div>
                    <div className="chart-bar" style={{ height: "70%" }}></div>
                    <div className="chart-bar" style={{ height: "95%" }}></div>
                    <div className="chart-bar" style={{ height: "78%" }}></div>
                    <div className="chart-bar" style={{ height: "90%" }}></div>
                    <div className="chart-bar" style={{ height: "82%" }}></div>
                    <div className="chart-bar" style={{ height: "88%" }}></div>
                    <div className="chart-bar" style={{ height: "100%" }}></div>
                    <div className="chart-bar" style={{ height: "72%" }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Hero Benefits */}
          <div className="hero-benefits">
            <div className="benefit-item">
              <div className="benefit-icon">
                <Smartphone size={24} />
              </div>
              <div className="benefit-text">
                <h4>Sistema 100% online</h4>
                <p>Acesse de qualquer dispositivo</p>
              </div>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">
                <Clock size={24} />
              </div>
              <div className="benefit-text">
                <h4>Disponível 24/7</h4>
                <p>Sem interrupções ou manutenção</p>
              </div>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">
                <Shield size={24} />
              </div>
              <div className="benefit-text">
                <h4>Dados seguros</h4>
                <p>Backup automático diário</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vantagens Section */}
      <section className="advantages-section" id="funcionalidades">
        <div className="landing-container">
          <div className="section-header">
            <h2 className="section-title">Principais Diferenciais</h2>
            <p className="section-subtitle">
              Tudo que você precisa para gerenciar seu negócio de locações
            </p>
          </div>

          <div className="advantages-grid">
            <div className="advantage-card" style={{ background: "#fff" }}>
              <div
                className="advantage-icon"
                style={{
                  background:
                    "linear-gradient(135deg, #2F80ED 0%, #1e5ab8 100%)",
                  color: "white",
                }}
              >
                <Package size={32} />
              </div>
              <h3
                style={{
                  color: "#1e3c72",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                }}
              >
                Gestão de Estoque
              </h3>
              <p style={{ color: "#2d3748", fontSize: "0.9375rem" }}>
                Controle completo do inventário em tempo real com alertas de
                baixo estoque
              </p>
            </div>

            <div className="advantage-card" style={{ background: "#fff" }}>
              <div
                className="advantage-icon"
                style={{
                  background:
                    "linear-gradient(135deg, #2F80ED 0%, #1e5ab8 100%)",
                  color: "white",
                }}
              >
                <Calendar size={32} />
              </div>
              <h3
                style={{
                  color: "#1e3c72",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                }}
              >
                Controle de Reservas
              </h3>
              <p style={{ color: "#2d3748", fontSize: "0.9375rem" }}>
                Sistema inteligente de reservas com calendário visual e
                notificações automáticas
              </p>
            </div>

            <div className="advantage-card" style={{ background: "#fff" }}>
              <div
                className="advantage-icon"
                style={{
                  background:
                    "linear-gradient(135deg, #2F80ED 0%, #1e5ab8 100%)",
                  color: "white",
                }}
              >
                <Users size={32} />
              </div>
              <h3
                style={{
                  color: "#1e3c72",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                }}
              >
                Atendimento ao Cliente
              </h3>
              <p style={{ color: "#2d3748", fontSize: "0.9375rem" }}>
                Gerencie clientes, histórico e preferências em um só lugar
              </p>
            </div>

            <div className="advantage-card" style={{ background: "#fff" }}>
              <div
                className="advantage-icon"
                style={{
                  background:
                    "linear-gradient(135deg, #2F80ED 0%, #1e5ab8 100%)",
                  color: "white",
                }}
              >
                <TrendingUp size={32} />
              </div>
              <h3
                style={{
                  color: "#1e3c72",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                }}
              >
                Relatórios Inteligentes
              </h3>
              <p style={{ color: "#2d3748", fontSize: "0.9375rem" }}>
                Análises detalhadas e dashboards para decisões estratégicas
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="landing-container">
          <div className="section-header">
            <h2 className="section-title">Funcionalidades Completas</h2>
            <p className="section-subtitle">
              Todas as ferramentas necessárias para sua operação
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-item" style={{ background: "#fff" }}>
              <div
                className="feature-icon-small"
                style={{
                  background:
                    "linear-gradient(135deg, #e8f2ff 0%, #d4e7ff 100%)",
                  color: "#2F80ED",
                }}
              >
                <CheckCircle size={24} />
              </div>
              <div className="feature-text">
                <h4
                  style={{
                    color: "#1e3c72",
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  Cadastro Rápido
                </h4>
                <p style={{ color: "#2d3748", fontSize: "0.875rem" }}>
                  Cadastre produtos, clientes e reservas em segundos
                </p>
              </div>
            </div>

            <div className="feature-item" style={{ background: "#fff" }}>
              <div
                className="feature-icon-small"
                style={{
                  background:
                    "linear-gradient(135deg, #e8f2ff 0%, #d4e7ff 100%)",
                  color: "#2F80ED",
                }}
              >
                <Clock size={24} />
              </div>
              <div className="feature-text">
                <h4
                  style={{
                    color: "#1e3c72",
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  Controle de Prazos
                </h4>
                <p style={{ color: "#2d3748", fontSize: "0.875rem" }}>
                  Alertas automáticos de devoluções e renovações
                </p>
              </div>
            </div>

            <div className="feature-item" style={{ background: "#fff" }}>
              <div
                className="feature-icon-small"
                style={{
                  background:
                    "linear-gradient(135deg, #e8f2ff 0%, #d4e7ff 100%)",
                  color: "#2F80ED",
                }}
              >
                <Shield size={24} />
              </div>
              <div className="feature-text">
                <h4
                  style={{
                    color: "#1e3c72",
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  Segurança Total
                </h4>
                <p style={{ color: "#2d3748", fontSize: "0.875rem" }}>
                  Dados criptografados e backup automático
                </p>
              </div>
            </div>

            <div className="feature-item" style={{ background: "#fff" }}>
              <div
                className="feature-icon-small"
                style={{
                  background:
                    "linear-gradient(135deg, #e8f2ff 0%, #d4e7ff 100%)",
                  color: "#2F80ED",
                }}
              >
                <BarChart3 size={24} />
              </div>
              <div className="feature-text">
                <h4
                  style={{
                    color: "#1e3c72",
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  Dashboards Visuais
                </h4>
                <p style={{ color: "#2d3748", fontSize: "0.875rem" }}>
                  Visualize métricas importantes em tempo real
                </p>
              </div>
            </div>

            <div className="feature-item" style={{ background: "#fff" }}>
              <div
                className="feature-icon-small"
                style={{
                  background:
                    "linear-gradient(135deg, #e8f2ff 0%, #d4e7ff 100%)",
                  color: "#2F80ED",
                }}
              >
                <Smartphone size={24} />
              </div>
              <div className="feature-text">
                <h4
                  style={{
                    color: "#1e3c72",
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  Acesso Mobile
                </h4>
                <p style={{ color: "#2d3748", fontSize: "0.875rem" }}>
                  Gerencie seu negócio de qualquer dispositivo
                </p>
              </div>
            </div>

            <div className="feature-item" style={{ background: "#fff" }}>
              <div
                className="feature-icon-small"
                style={{
                  background:
                    "linear-gradient(135deg, #e8f2ff 0%, #d4e7ff 100%)",
                  color: "#2F80ED",
                }}
              >
                <Zap size={24} />
              </div>
              <div className="feature-text">
                <h4
                  style={{
                    color: "#1e3c72",
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  Performance
                </h4>
                <p style={{ color: "#2d3748", fontSize: "0.875rem" }}>
                  Sistema rápido e otimizado para alta demanda
                </p>
              </div>
            </div>

            <div className="feature-item" style={{ background: "#fff" }}>
              <div
                className="feature-icon-small"
                style={{
                  background:
                    "linear-gradient(135deg, #e8f2ff 0%, #d4e7ff 100%)",
                  color: "#2F80ED",
                }}
              >
                <FileText size={24} />
              </div>
              <div className="feature-text">
                <h4
                  style={{
                    color: "#1e3c72",
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  Contratos Digitais
                </h4>
                <p style={{ color: "#2d3748", fontSize: "0.875rem" }}>
                  Gere e gerencie contratos digitalmente
                </p>
              </div>
            </div>

            <div className="feature-item" style={{ background: "#fff" }}>
              <div
                className="feature-icon-small"
                style={{
                  background:
                    "linear-gradient(135deg, #e8f2ff 0%, #d4e7ff 100%)",
                  color: "#2F80ED",
                }}
              >
                <Users size={24} />
              </div>
              <div className="feature-text">
                <h4
                  style={{
                    color: "#1e3c72",
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  Fluxo de Funcionários
                </h4>
                <p style={{ color: "#2d3748", fontSize: "0.875rem" }}>
                  Acesso dedicado para a equipe com telas e permissões próprias
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Nao vou mostrar os planos por enquanto so futuramente */}
      {/* <PlansSection /> */}

      {/* FAQ Section */}
      <section className="faq-section" id="faq">
        <div className="landing-container">
          <div className="faq-header">
            <p className="faq-label">FAQ</p>
            <h2 className="faq-title">Perguntas frequentes</h2>
          </div>

          <div className="faq-list">
            <div className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(0)}>
                <span>Como funciona a locação no SystemLoc?</span>
                <span className="faq-icon">
                  {openFaqIndex === 0 ? "−" : "+"}
                </span>
              </button>
              <div
                className={`faq-answer ${openFaqIndex === 0 ? "active" : ""}`}
              >
                <p>
                  Você cadastra suas peças, define preços e disponibilidade.
                  Quando um cliente solicita uma locação, o sistema reserva
                  automaticamente e controla prazos de devolução.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(1)}>
                <span>Posso testar o sistema gratuitamente?</span>
                <span className="faq-icon">
                  {openFaqIndex === 1 ? "−" : "+"}
                </span>
              </button>
              <div
                className={`faq-answer ${openFaqIndex === 1 ? "active" : ""}`}
              >
                <p>
                  Sim! Oferecemos 14 dias de teste gratuito, sem necessidade de
                  cartão de crédito. Você pode explorar todas as funcionalidades
                  e cancelar quando quiser.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(2)}>
                <span>Preciso instalar algum programa?</span>
                <span className="faq-icon">
                  {openFaqIndex === 2 ? "−" : "+"}
                </span>
              </button>
              <div
                className={`faq-answer ${openFaqIndex === 2 ? "active" : ""}`}
              >
                <p>
                  Não! O SystemLoc é 100% online. Acesse de qualquer dispositivo
                  com internet, seja computador, tablet ou celular.
                </p>
              </div>
            </div>

            {/* <div className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(3)}>
                <span>Quantas peças posso cadastrar?</span>
                <span className="faq-icon">
                  {openFaqIndex === 3 ? "−" : "+"}
                </span>
              </button>
              <div
                className={`faq-answer ${openFaqIndex === 3 ? "active" : ""}`}
              >
                <p>
                  No Plano Essencial, você pode cadastrar até 100 peças. Para
                  quem precisa de mais liberdade, o Plano Ilimitado não possui
                  restrições.
                </p>
              </div>
            </div> */}

            <div className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(4)}>
                <span>Meus dados ficam seguros?</span>
                <span className="faq-icon">
                  {openFaqIndex === 4 ? "−" : "+"}
                </span>
              </button>
              <div
                className={`faq-answer ${openFaqIndex === 4 ? "active" : ""}`}
              >
                <p>
                  Absolutamente! Utilizamos criptografia de ponta e fazemos
                  backup automático diário de todos os dados. Seus dados estão
                  seguros conosco.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(5)}>
                <span>Como funciona o suporte?</span>
                <span className="faq-icon">
                  {openFaqIndex === 5 ? "−" : "+"}
                </span>
              </button>
              <div
                className={`faq-answer ${openFaqIndex === 5 ? "active" : ""}`}
              >
                <p>
                  Oferecemos suporte por email e chat durante horário comercial.
                  Clientes premium têm acesso prioritário e suporte estendido.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(6)}>
                <span>Como funciona o fluxo dos funcionários?</span>
                <span className="faq-icon">
                  {openFaqIndex === 6 ? "−" : "+"}
                </span>
              </button>
              <div
                className={`faq-answer ${openFaqIndex === 6 ? "active" : ""}`}
              >
                <p>
                  Para quem tem funcionários, a tela do funcionário permite
                  fazer um pré-cadastro antes do cliente chegar na recepção.
                  Assim, a equipe pode registrar as medidas e os produtos que
                  serão reservados, agilizando o atendimento e garantindo mais
                  qualidade no serviço.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer" id="contato">
        <div className="landing-container">
          <div className="footer-content">
            <div className="footer-column">
              <h3 className="footer-logo">
                <span className="logo-system">System</span>
                <span className="logo-loc">Loc</span>
              </h3>
              <p>
                Sistema completo de gestão para locações com tecnologia de
                ponta.
              </p>
            </div>

            <div className="footer-column">
              <h4>Produto</h4>
              <ul>
                <li>
                  <a href="#funcionalidades">Funcionalidades</a>
                </li>
                {/* <li>
                  <a href="#planos">Planos</a>
                </li> */}
                {/* <li>
                  <a href="#contato">Contato</a>
                </li> */}
              </ul>
            </div>

            <div className="footer-column">
              <h4>Legal</h4>
              <ul>
                <li>
                  <a
                    href="#privacidade"
                    onClick={(event) => {
                      event.preventDefault();
                      onOpenPrivacy?.();
                    }}
                  >
                    Privacidade
                  </a>
                </li>
                <li>
                  <a
                    href="#termos"
                    onClick={(event) => {
                      event.preventDefault();
                      onOpenTerms?.();
                    }}
                  >
                    Termos de Uso
                  </a>
                </li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Contato</h4>
              <ul>
                <li>contato.systemloc@gmail.com</li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2026 SystemLoc. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
